import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ethers } from 'ethers';
import {
  ArrowLeft,
  BrainCircuit,
  Mic,
  Music,
  Film,
  Brush,
  Sparkles,
  Wand2,
  Search,
  Save,
  Trash2,
  PlusCircle,
  UploadCloud,
  File as FileIcon,
  Loader2,
  Database,
  Wallet,
  ShieldAlert,
  Image as ImageIcon,
  X,
  Network,
  Info,
  GitCommit,
  RefreshCw,
  CalendarDays,
  Users,
  Copy,
  Download,
  FileText
} from 'lucide-react';
import { useDrag, useDrop } from 'react-dnd';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext'; // IMPORT useAuth
import { IPFSAsset } from '../types';
import { useWallet } from '../contexts/WalletContext';
import {
  analyzeFileContent,
  analyzeNFTMetadata,
  ADVANCED_MODERATION_WARNING_TEXT,
} from '../services/advancedModerationService';
import { contractAddresses } from '../data/contractAddresses';
import KalaKrutNFT from '../abis/KalaKrutNFT.json';
import Fractionalizer from '../abis/Fractionalizer.json';
import ContractFactory from '../abis/ContractFactory.json';
import { uploadToIPFS } from '../utils/ipfs';
import { runAIGeneration } from '../services/geminiService';

// --- Type Definitions and Constants ---

const ItemTypes = { TOOL: 'tool' };

interface ToolDefinition {
  name: string;
  icon: React.ElementType;
  disabled?: boolean;
}

const tools: ToolDefinition[] = [
  { name: 'AI Idea Gen', icon: BrainCircuit, disabled: false },
  { name: 'AI Lyricist', icon: Mic, disabled: false },
  { name: 'AI Composer', icon: Music, disabled: false },
  { name: 'AI Storyboard', icon: Film, disabled: false },
  { name: 'AI Art Style', icon: Brush, disabled: false },
  { name: 'AI Enhancer', icon: Sparkles, disabled: false },
  { name: 'Magic Mix', icon: Wand2, disabled: false },
  { name: 'Asset Search', icon: Search, disabled: false },
];

const getToolInputs = (name: string) => {
    switch (name) {
        case 'AI Lyricist': return [{ name: 'topic', label: 'Topic / Theme', type: 'textarea', placeholder: 'e.g., A journey through a cyberpunk city at night' }];
        case 'AI Composer': return [
            { name: 'mood', label: 'Mood', type: 'text', placeholder: 'e.g., Epic, melancholic, upbeat' },
            { name: 'instruments', label: 'Key Instruments', type: 'text', placeholder: 'e.g., Synth, piano, orchestra' },
            { name: 'tempo', label: 'Tempo', type: 'text', placeholder: 'e.g., Slow, 120bpm, fast' },
        ];
        case 'AI Storyboard': return [{ name: 'scene', label: 'Scene or Action', type: 'textarea', placeholder: 'e.g., A character discovering a hidden portal' }];
        case 'AI Art Style': return [{ name: 'concept', label: 'Base Concept or Keywords', type: 'textarea', placeholder: 'e.g., Art Deco, spaceships, neon glow' }];
        case 'Asset Search': return [{ name: 'asset_description', label: 'Describe the assets you need', type: 'textarea', placeholder: 'e.g., 8-bit sound effects for a platformer game' }];
        case 'AI Idea Gen':
        case 'AI Enhancer':
        case 'Magic Mix': return [{ name: 'custom_context', label: 'Optional Context', type: 'textarea', placeholder: 'Add any specific details, keywords, or instructions to guide the AI...' }];
        default: return [];
    }
};

// --- Modals ---

interface MintConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  asset: IPFSAsset | null;
}

const MintConfirmationModal: React.FC<MintConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  asset,
}) => {
  const { getChainId, switchNetwork } = useWallet();
  const [networkName, setNetworkName] = useState<string | undefined>();

  useEffect(() => {
    const chainId = getChainId();
    const name = chainId === 80002 ? 'Amoy' : chainId === 137 ? 'Polygon' : 'Other';
    setNetworkName(name);
  }, [getChainId, isOpen]);

  if (!isOpen || !asset) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-kala-950 border border-kala-800 rounded-2xl p-8 w-full max-w-lg shadow-2xl"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Sparkles className="text-kala-secondary" />
            Confirm Mint
          </h2>
          <button onClick={onClose} className="text-kala-500 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="bg-kala-900 p-4 rounded-lg border border-kala-800">
            <h3 className="font-semibold text-white mb-2">Asset Details</h3>
            <p className="text-sm text-kala-300 break-words"><strong>Name:</strong> {asset.name}</p>
            <p className="text-sm text-kala-400"><strong>Type:</strong> {asset.type} ({asset.size})</p>
            <p className="text-sm text-kala-500 font-mono text-xs mt-1 break-all"><strong>CID:</strong> {asset.cid}</p>
          </div>
          <div className="bg-kala-900 p-4 rounded-lg border border-kala-800">
            <h3 className="font-semibold text-white mb-2">Transaction Details</h3>
             <div className="flex items-center gap-3 mb-2">
                <Network className="w-5 h-5 text-kala-secondary" />
                <div>
                    <p className="text-sm font-bold text-kala-200">Network</p>
                    <p className="text-sm text-kala-300">{networkName || 'Unknown'}</p>
                </div>
            </div>
            {networkName !== 'Amoy' && (
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 flex flex-col items-start gap-3 mt-3">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                  <div className="flex-grow">
                    <p className="text-sm font-semibold text-yellow-200">Incorrect Network</p>
                    <p className="text-xs text-yellow-300 mt-1">
                      This feature requires the Amoy testnet. Please switch your network to proceed.
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => switchNetwork('80002')}
                  className="ml-auto mt-2 flex items-center gap-2 text-xs bg-yellow-500/20 text-yellow-100 font-bold py-2 px-3 rounded-md hover:bg-yellow-500/40 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Switch to Amoy
                </button>
              </div>
            )}
            <div className="flex items-center gap-3 mt-3">
                <Info className="w-5 h-5 text-kala-secondary" />
                <div>
                    <p className="text-sm font-bold text-kala-200">Estimated Gas Fee</p>
                    <p className="text-sm text-kala-300">~0.01 MATIC (placeholder)</p>
                </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-4">
          <button onClick={onClose} className="px-6 py-2 bg-kala-800 text-white font-semibold rounded-lg hover:bg-kala-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={networkName !== 'Amoy'}
            className="px-6 py-2 bg-kala-secondary text-white font-bold rounded-lg hover:bg-cyan-400 transition-colors shadow-[0_0_20px_rgba(56,189,248,0.3)] disabled:bg-kala-700 disabled:cursor-not-allowed disabled:shadow-none"
          >
            Confirm Mint
          </button>
        </div>
      </motion.div>
    </div>
  );
};

type FractionalizeType = 'ownership' | 'time';
interface FractionalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (details: { type: FractionalizeType; fractions: number; totalDays?: number; dayToSell?: number; }) => void;
  asset: IPFSAsset | null;
  isProcessing: boolean;
}

const FractionalizeModal: React.FC<FractionalizeModalProps> = ({
  isOpen, onClose, onConfirm, asset, isProcessing
}) => {
  const [type, setType] = useState<FractionalizeType>('ownership');
  const [fractions, setFractions] = useState(1000);
  const [totalDays, setTotalDays] = useState(10);
  const [dayToSell, setDayToSell] = useState(5);

  if (!isOpen || !asset) return null;

  const handleConfirm = () => {
    if (type === 'ownership') {
      onConfirm({ type, fractions });
    } else {
      onConfirm({ type, fractions: totalDays, totalDays, dayToSell });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-kala-950 border border-kala-800 rounded-2xl p-8 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3"><GitCommit className="text-kala-accent" /> Fractionalize NFT</h2>
          <button onClick={onClose} className="text-kala-500 hover:text-white"><X size={24} /></button>
        </div>

        <div className="space-y-6">
          <div className="bg-kala-900 p-4 rounded-lg border border-kala-800">
            <p className="text-sm text-kala-300 break-words"><strong>Asset:</strong> {asset.name} (Token ID: {asset.tokenId})</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-kala-200 mb-2">Fractionalization Type</label>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-kala-800 p-1">
              <button onClick={() => setType('ownership')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${type === 'ownership' ? 'bg-kala-accent text-white' : 'text-kala-300 hover:bg-kala-700'}`}>Divide Ownership</button>
              <button onClick={() => setType('time')} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${type === 'time' ? 'bg-kala-accent text-white' : 'text-kala-300 hover:bg-kala-700'}`}>Sell Time-based Access</button>
            </div>
          </div>

          {type === 'ownership' ? (
            <div className="animate-in fade-in duration-300">
              <label htmlFor="fractions" className="block text-sm font-bold text-kala-200 mb-2 flex items-center gap-2"><Users size={16} /> Number of Ownership Fractions</label>
              <input id="fractions" type="number" value={fractions} onChange={(e) => setFractions(Number(e.target.value))} className="w-full bg-kala-950 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g., 1000" />
              <p className="text-xs text-kala-400 mt-2">This will create {fractions} fungible tokens, representing shared ownership or equity in this NFT.</p>
            </div>
          ) : (
            <div className="space-y-4 animate-in fade-in duration-300">
                <div>
                    <label htmlFor="totalDays" className="block text-sm font-bold text-kala-200 mb-2 flex items-center gap-2"><CalendarDays size={16} /> Total Event Duration (in days)</label>
                    <input id="totalDays" type="number" value={totalDays} onChange={(e) => setTotalDays(Number(e.target.value))} className="w-full bg-kala-950 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g., 10" />
                </div>
                <div>
                    <label htmlFor="dayToSell" className="block text-sm font-bold text-kala-200 mb-2">Which Day&apos;s Ticket to Sell?</label>
                    <input id="dayToSell" type="number" value={dayToSell} onChange={(e) => setDayToSell(Number(e.target.value))} className="w-full bg-kala-950 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="e.g., 5" />
                    <p className="text-xs text-kala-400 mt-2">You are creating a new NFT representing access for Day {dayToSell} of a {totalDays}-day event.</p>
                </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <button onClick={onClose} className="px-6 py-2 bg-kala-800 text-white font-semibold rounded-lg hover:bg-kala-700 transition-colors" disabled={isProcessing}>Cancel</button>
          <button onClick={handleConfirm} disabled={isProcessing || (type === 'ownership' && fractions <= 0) || (type === 'time' && (totalDays <= 0 || dayToSell <= 0 || dayToSell > totalDays))} className="px-6 py-2 bg-kala-accent text-white font-bold rounded-lg hover:bg-orange-400 transition-colors shadow-[0_0_20px_rgba(251,146,60,0.3)] disabled:bg-kala-700 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2">
            {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <GitCommit className="w-5 h-5" />}
            {isProcessing ? 'Processing...' : 'Confirm Fractionalize'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

interface AIPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (inputs: Record<string, string>, file?: File) => void;
  toolName: string | null;
  isGenerating: boolean;
}

const AIPromptModal: React.FC<AIPromptModalProps> = ({ isOpen, onClose, onSubmit, toolName, isGenerating }) => {
    // --- HOOKS AT THE TOP ---
    const [inputValues, setInputValues] = useState<Record<string, string>>({});
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const tool = useMemo(() => toolName ? tools.find(t => t.name === toolName) : null, [toolName]);
    const inputs = useMemo(() => tool ? getToolInputs(tool.name) : [], [tool]);

    useEffect(() => {
        if (isOpen) {
            const initialState: Record<string, string> = {};
            inputs.forEach(input => { initialState[input.name] = ''; });
            setInputValues(initialState);
            setUploadedFile(null);
        }
    }, [isOpen, inputs]);

    // --- EARLY RETURN AFTER HOOKS ---
    if (!isOpen || !tool) {
        return null;
    }

    // --- Component Logic & Handlers ---
    const Icon = tool.icon;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInputValues(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };
    const handleSubmit = () => onSubmit(inputValues, uploadedFile || undefined);
    const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) setUploadedFile(e.dataTransfer.files[0]);
    };
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) setUploadedFile(e.target.files[0]);
    };
    const removeFile = () => setUploadedFile(null);

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-kala-950 border border-kala-800 rounded-2xl p-8 w-full max-w-lg shadow-2xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Icon className="w-6 h-6 text-kala-secondary"/>
                {tool.name}
            </h2>
            <button onClick={onClose} className="text-kala-500 hover:text-white"><X size={24} /></button>
          </div>
          <div className="space-y-4">
            {inputs.map(input => (
                <div key={input.name}>
                    <label className="block text-sm font-bold text-kala-200 mb-2">{input.label}</label>
                    {input.type === 'textarea' ? <textarea name={input.name} value={inputValues[input.name] || ''} onChange={handleInputChange} placeholder={input.placeholder} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary resize-none" rows={4}/> : <input type={input.type} name={input.name} value={inputValues[input.name] || ''} onChange={handleInputChange} placeholder={input.placeholder} className="w-full bg-kala-900 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary"/>}
                </div>
            ))}
            <div className="mt-4">
                <label className="block text-sm font-bold text-kala-200 mb-2">Optional: Upload File for Context</label>
                {uploadedFile ? (
                    <div className="p-3 bg-kala-900 rounded-lg flex items-center justify-between border border-kala-700">
                        <div className="flex items-center gap-3 overflow-hidden"><FileIcon className="w-5 h-5 text-kala-300 flex-shrink-0" /><span className="text-sm text-white truncate" title={uploadedFile.name}>{uploadedFile.name}</span></div>
                        <button onClick={removeFile} className="text-kala-400 hover:text-white p-1 rounded-full flex-shrink-0"><X size={16} /></button>
                    </div>
                ) : (
                    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-all relative ${isDragging ? 'border-kala-secondary bg-kala-secondary/10' : 'border-kala-700 bg-kala-800/30 hover:bg-kala-800/50'}`}>
                        <UploadCloud className="w-8 h-8 text-kala-400 mb-2" /><h3 className="text-sm font-bold text-white mb-1">Drag & Drop File</h3><p className="text-xs text-kala-400">or</p><input type="file" id="modal-file-upload" className="sr-only" onChange={handleFileSelect} /><label htmlFor="modal-file-upload" className="text-sm text-kala-secondary font-semibold hover:underline cursor-pointer mt-1">Browse Files</label>
                    </div>
                )}
            </div>
        </div>
          <div className="mt-8 flex justify-end gap-4">
            <button onClick={onClose} disabled={isGenerating} className="px-6 py-2 bg-kala-800 text-white font-semibold rounded-lg hover:bg-kala-700 transition-colors disabled:opacity-50">Cancel</button>
            <button onClick={handleSubmit} disabled={isGenerating} className="px-6 py-2 bg-kala-secondary text-white font-bold rounded-lg hover:bg-cyan-400 transition-colors shadow-[0_0_20px_rgba(56,189,248,0.3)] disabled:bg-kala-700 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2">
              {isGenerating ? <Loader2 className="animate-spin w-5 h-5"/> : <Sparkles className="w-4 h-4" />}
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </motion.div>
      </div>
    );
};

// --- Components ---

interface CreativeStudioProps {
  onBack: () => void;
  onBlockUser: () => void;
}

interface ToolProps extends ToolDefinition {
  onClick: () => void;
}

const Tool: React.FC<ToolProps> = ({ name, icon: Icon, disabled, onClick }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TOOL,
    item: { name },
    canDrag: !disabled,
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));

  return (
    <div ref={drag} onClick={!disabled ? onClick : undefined} style={{ opacity: isDragging ? 0.5 : 1 }} className={`flex flex-col items-center justify-center p-4 bg-kala-800 rounded-lg border border-kala-700 transition-all duration-200 relative ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-kala-700/50 cursor-pointer transform hover:scale-105'}`}>
      <Icon className={`w-8 h-8 mb-2 ${disabled ? 'text-kala-600' : 'text-kala-secondary'}`} />
      <span className={`text-sm text-center ${disabled ? 'text-kala-500' : 'text-slate-300'}`}>{name}</span>
      {disabled && <span className="absolute top-1 right-1 text-[9px] bg-yellow-500 text-black font-bold px-1.5 py-0.5 rounded">Soon</span>}
    </div>
  );
};

interface CanvasToolProps extends ToolDefinition { 
  id: number;
}

const CanvasTool: React.FC<CanvasToolProps> = ({ name, icon: Icon }) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-kala-950 rounded-lg border border-kala-700/50 shadow-md">
      <Icon className="w-6 h-6 text-kala-accent" />
      <span className="text-slate-200 font-medium">{name}</span>
    </div>
  );
};

const createPromptForTool = (toolName: string, _project: string, _contextTools: CanvasToolProps[], customInputs: Record<string, string>, fileContext?: string): string => {
  // This function is now focused on generating a direct, user-centric prompt.
  const filePrompt = fileContext ? `\n\nUse the following file content as context:\n---\n${fileContext.substring(0, 3000)}\n---\n` : '';
  const customContext = customInputs.custom_context ? `\n\nUser has provided this specific context: ${customInputs.custom_context}\n` : '';

  // Base prompt is direct and gives the AI a clear role.
  const basePrompt = `You are a creative assistant. Your current task is to act as an "${toolName}".${customContext}${filePrompt}\n\nYour instructions are:\n`;

  switch (toolName) {
    case 'AI Idea Gen':
      const ideaContext = customInputs.custom_context || "a new creative project";
      return basePrompt + `Generate 3 distinct and imaginative concepts for \"${ideaContext}\".`;

    case 'AI Lyricist':
      const topic = customInputs.topic || "an interesting theme";
      return basePrompt + `Write a four-line chorus about \"${topic}\". The tone should be inspiring and modern.`;

    case 'AI Composer':
      const mood = customInputs.mood || 'interesting';
      const instruments = customInputs.instruments || 'a mix of digital and analog instruments';
      const tempo = customInputs.tempo || 'mid-tempo';
      return basePrompt + `Describe a musical theme. The mood should be ${mood}, featuring ${instruments}, at a ${tempo} tempo.`;

    case 'AI Storyboard':
      const scene = customInputs.scene || 'a compelling scene';
      return basePrompt + `Outline a three-shot video sequence for \"${scene}\". For each shot, briefly describe the camera angle, subject, and action.`;

    case 'AI Art Style':
      const concept = customInputs.concept || 'a unique visual style';
      return basePrompt + `Define a unique visual art style based on the concept of \"${concept}\". Describe its main characteristics and one or two real-world art movements that influence it.`;

    case 'AI Enhancer':
      const enhanceContext = customInputs.custom_context || "the provided context";
      return basePrompt + `Analyze the following subject: \"${enhanceContext}\". Suggest a single, unexpected feature or twist to make it more compelling.`;

    case 'Magic Mix':
      const mixContext = customInputs.custom_context || "the user's idea";
      return basePrompt + `Synthesize the following idea into a cohesive and exciting project summary: \"${mixContext}\". Describe how the potential elements could work together.`;

    case 'Asset Search':
      const description = customInputs.asset_description || 'digital assets for a project';
      return basePrompt + `Based on the description \"${description}\", list 3 essential digital assets needed. For example: 'Character concept art', 'Ambient soundscape', 'UI icon pack'.`;

    default:
      return `Generate a creative output using the tool: "${toolName}".`;
  }
};

const CreativeStudio: React.FC<CreativeStudioProps> = ({ onBack, onBlockUser }) => {
  const { notify } = useToast();
  const { token } = useAuth(); // ADDED useAuth hook
  const [canvasTools, setCanvasTools] = useState<CanvasToolProps[]>([]);
  const [projectName, setProjectName] = useState('My Awesome Project');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { isConnected, provider, open, getChainId } = useWallet();
  
  const [isMintModalOpen, setIsMintModalOpen] = useState(false);
  const [isFractionalizeModalOpen, setIsFractionalizeModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);

  const [selectedAsset, setSelectedAsset] = useState<IPFSAsset | null>(null);
  const [selectedToolName, setSelectedToolName] = useState<string | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [aiOutput, setAiOutput] = useState('Awaiting instructions. Drag or click a tool to begin.');
  const [assets, setAssets] = useState<IPFSAsset[]>([
    { id: 'asset-1', name: 'Album_Artwork_Final.png', type: 'Image', size: '2.4 MB', cid: 'QmXoypizjW3WknFiJnKLwHCnL72vedxjQkDDP1mXWo6uco', url: '#', status: 'Pinned', timestamp: '2023-10-14 14:30' },
    // FIX for TS2322: Changed 'Event' to 'Document' to match the allowed types for IPFSAsset.
    { id: '2', name: 'Multi-Day Event Ticket', type: 'Document', size: '1 KB', cid: 'QmZ4tDuvesjW3WknFiJnKLwHCnL72vedxjQkDD1mXWo6uco', url: '#', status: 'Minted', tokenId: 1, contractAddress: contractAddresses.KalaKrutNFT },
  ]);

  useEffect(() => {
    if (selectedToolName) setIsPromptModalOpen(true);
  }, [selectedToolName]);

  const handleToolInteraction = (toolName: string) => {
    const tool = tools.find(t => t.name === toolName);
    if (tool?.disabled) {
      notify('This tool is not yet active.', 'warn');
      return;
    }
    setSelectedToolName(toolName);
  };

  const handlePromptSubmit = async (inputs: Record<string, string>, file?: File) => {
    const tool = tools.find(t => t.name === selectedToolName);
    if (!tool) return;

    let fileContext: string | undefined = undefined;

    setIsPromptModalOpen(false);
    setSelectedToolName(null);
    
    const nextCanvasTools = [...canvasTools, { ...tool, id: Date.now() }];
    setCanvasTools(nextCanvasTools);
    
    setIsGenerating(true);
    setAiOutput(`🤖 Preparing prompt...`);
    notify(`${tool.name} added to canvas!`, 'success');

    if (file) {
      try {
        notify('Reading file for context...', 'info');
        if (file.type.startsWith('text/')) {
          fileContext = await file.text();
        } else {
          fileContext = `A file named "${file.name}" of type "${file.type}" and size ${file.size} bytes has been uploaded as context.`;
          notify('Non-text file provided. File metadata will be used in prompt.', 'warn');
        }
      } catch {
        notify('Could not read the uploaded file.', 'error');
        setCanvasTools(canvasTools);
        setIsGenerating(false);
        return;
      }
    }

    setAiOutput(`🤖 Generating with ${tool.name}...`);
    const prompt = createPromptForTool(tool.name, projectName, nextCanvasTools, inputs, fileContext);

    try {
      console.log("Sending prompt to backend:", { tool: tool.name, promptLength: prompt.length });
      
      // CORRECTED: Pass the auth token to the generation service
      const result = await runAIGeneration(prompt, token);

      // We need to check if the result is a valid, non-empty string.
      if (!result || typeof result !== 'string' || result.trim().length === 0) {
        console.error("AI Generation failed: Invalid or empty response.", result);
        throw new Error('AI service returned an invalid or empty response.');
      }
      
      console.log("Received AI result:", { length: result.length, preview: result.substring(0, 100) });
      setAiOutput(result);
    } catch (error) {
      console.error("AI Generation failed:", error);
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      notify(`AI Generation failed: ${message}`, 'error');
      setAiOutput(`❌ Error using ${tool.name}: ${message}\n\nPlease try again or check your prompt.`);
    } finally {
      setIsGenerating(false);
    }
  };

  const [, drop] = useDrop(() => ({ accept: ItemTypes.TOOL, drop: (item: { name: string }) => handleToolInteraction(item.name) }), []);

  const handleClearCanvas = () => { 
    setCanvasTools([]); 
    setAiOutput('Canvas cleared. Drag or click a tool to begin.');
    notify('Canvas cleared.', 'info'); 
  };

  const handleSaveProject = () => notify(`Project "${projectName}" saved!`, 'success');
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) { await uploadAndPinAsset(files[0]); }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { await uploadAndPinAsset(file); }
  };

  const uploadAndPinAsset = async (file: File) => {
    setIsUploading(true);
    notify('Scanning file for violations...', 'info');

    try {
        // *** ADVANCED MODERATION CHECK ***
        const analysis = await analyzeFileContent(file);
        if (analysis.violation) {
            notify(`Upload blocked: ${analysis.reason}`, 'error');
            onBlockUser();
            setIsUploading(false);
            return;
        }

        notify('File passed moderation. Uploading to IPFS...', 'info');
        const url = await uploadToIPFS(file);
        const newAsset: IPFSAsset = { id: Date.now().toString(), name: file.name, type: file.type.split('/')[0], size: `${(file.size / 1024 / 1024).toFixed(2)} MB`, cid: url.split('/').pop() || '', url, status: 'Pinned', timestamp: new Date().toLocaleString() };
        setAssets((prev) => [newAsset, ...prev]);
        notify('File pinned to IPFS!', 'success');
    } catch (error: unknown) { 
        if (error instanceof Error) {
            notify(`Upload failed: ${error.message}`, 'error'); 
        } else {
            notify('An unknown error occurred during upload', 'error');
        }
    } finally { 
        setIsUploading(false); 
    }
  };

  const handleMintRequest = (assetId: string) => {
    const asset = assets.find(a => a.id === assetId);
    if (asset) { setSelectedAsset(asset); setIsMintModalOpen(true); }
  };
  
  const handleConfirmMint = async () => {
    if (!selectedAsset || !provider) return notify('Asset or provider not ready', 'error');
    if (!isConnected) { await open(); return; }
    if (getChainId() !== 80002) return notify('Please switch to Amoy testnet to mint.', 'error');

    const metadata = { name: selectedAsset.name, description: selectedAsset.type, image: selectedAsset.url };
    
    // *** ADVANCED MODERATION CHECK (NFT) ***
    const analysis = analyzeNFTMetadata(metadata);
    if (analysis.violation) {
        notify(`Minting blocked: ${analysis.reason}`, 'error');
        onBlockUser();
        return;
    }

    setIsMintModalOpen(false);
    setIsProcessing(true);
    notify('Minting transaction initiated...', 'info');
    try {
      const signer = await provider.getSigner();
      const metadataUrl = await uploadToIPFS(new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      notify('Metadata uploaded', 'info');
      
      const nftContract = new ethers.Contract(contractAddresses.KalaKrutNFT, KalaKrutNFT.abi, signer);
      const tx = await nftContract.safeMint(await signer.getAddress(), metadataUrl);
      const receipt = await tx.wait();
      const transferEvent = receipt.events?.find((e: { event: string; }) => e.event === 'Transfer');
      const tokenId = transferEvent?.args?.tokenId.toNumber();

      setAssets(prev => prev.map(a => a.id === selectedAsset.id ? { ...a, status: 'Minted', tokenId, contractAddress: contractAddresses.KalaKrutNFT } : a));
      notify(`NFT Minted! Token ID: ${tokenId}`, 'success');
    } catch (error: unknown) { 
        console.error("Minting failed:", error); 
        if (error instanceof Error) {
            notify(`Minting Error: ${error.message}`, 'error');
        } else {
            notify('An unknown minting error occurred', 'error');
        }
    } finally { 
        setIsProcessing(false); 
        setSelectedAsset(null); 
    }
  };

  const handleFractionalizeRequest = (asset: IPFSAsset) => {
    setSelectedAsset(asset);
    setIsFractionalizeModalOpen(true);
  };

  const handleConfirmFractionalize = async (details: { type: FractionalizeType; fractions: number; totalDays?: number; dayToSell?: number; }) => {
    if (!selectedAsset || !selectedAsset.tokenId || !provider) return notify('Selected asset is not a valid NFT.', 'error');

    setIsFractionalizeModalOpen(false);
    setIsProcessing(true);
    notify(`Initiating ${details.type} fractionalization...`, 'info');

    try {
      const signer = await provider.getSigner();
      // FIX for TS2339: Casting to 'any' to bypass a paradoxical TypeScript error where 'abi' is said not to exist on a type that clearly has it.
      const factory = new ethers.Contract(contractAddresses.ContractFactory, (ContractFactory as any).abi, signer);

      if (details.type === 'ownership') {
        const tx = await factory.createFractionalizer( `${selectedAsset.name} Fractions`, `FRAC-${selectedAsset.name.substring(0, 4).toUpperCase()}`, selectedAsset.contractAddress, selectedAsset.tokenId, details.fractions );
        const receipt = await tx.wait();
        const createEvent = receipt.events.find((e: { event: string; }) => e.event === 'ContractCreated');
        const fractionalizerAddress = createEvent.args.contractAddress;
        
        const nftContract = new ethers.Contract(selectedAsset.contractAddress!, KalaKrutNFT.abi, signer);
        await (await nftContract.approve(fractionalizerAddress, selectedAsset.tokenId)).wait();
        
        const fractionalizer = new ethers.Contract(fractionalizerAddress, Fractionalizer.abi, signer);
        await (await fractionalizer.depositNFT()).wait();

        setAssets(prev => prev.map(a => a.id === selectedAsset.id ? { ...a, status: 'Fractionalized', fractionalizerAddress } : a));
        notify('NFT successfully fractionalized into shares!', 'success');
      } else { // Time-based
        const newTicketName = `${selectedAsset.name} - Day ${details.dayToSell}`;
        notify(`A new NFT representing Day ${details.dayToSell} has been created.`, 'success');
        // FIX for TS2322: Changed 'Event Ticket' to 'Document' to match the allowed types for IPFSAsset.
        const newAsset: IPFSAsset = { id: Date.now().toString(), name: newTicketName, type: 'Document', size: '0.1 MB', cid: 'Qmock...timesplit', url: '#', status: 'Minted', tokenId: Math.floor(Math.random() * 10000), contractAddress: ethers.Wallet.createRandom().address };
        setAssets(prev => [newAsset, ...prev]);
      }
    } catch (error: unknown) { 
        console.error("Fractionalization failed:", error); 
        if (error instanceof Error) {
            notify(`Error: ${error.message}`, 'error');
        } else {
            notify('An unknown fractionalization error occurred.', 'error');
        }
    } finally { 
        setIsProcessing(false); 
        setSelectedAsset(null); 
    }
  };

  const handleCopyOutput = () => {
    if (isGenerating || canvasTools.length === 0) return;
    navigator.clipboard.writeText(aiOutput);
    notify('AI output copied to clipboard!', 'success');
  };

  const handleDownloadOutput = () => {
    if (isGenerating || canvasTools.length === 0) return;
    const blob = new Blob([aiOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName.replace(/\s+/g, '_')}_AI_Output.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    notify('AI output download started.', 'success');
  };

  return (
    <div className="min-h-screen bg-kala-900 text-white p-4 sm:p-6 md:p-8">
      <MintConfirmationModal isOpen={isMintModalOpen} onClose={() => setIsMintModalOpen(false)} onConfirm={handleConfirmMint} asset={selectedAsset} />
      <FractionalizeModal isOpen={isFractionalizeModalOpen} onClose={() => setIsFractionalizeModalOpen(false)} onConfirm={handleConfirmFractionalize} asset={selectedAsset} isProcessing={isProcessing} />
      
      {/* Conditionally render the modal in the parent to avoid hook order issues */}
      <AIPromptModal 
        isOpen={isPromptModalOpen}
        onClose={() => { setIsPromptModalOpen(false); setSelectedToolName(null); }}
        onSubmit={handlePromptSubmit}
        toolName={selectedToolName}
        isGenerating={isGenerating}
      />
      
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Creative Studio</h1>
              <p className="text-slate-400">Drag AI tools, upload assets, and build your project.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-kala-500 bg-kala-800 px-3 py-1 rounded-full border border-kala-700"><Database className="w-3 h-3" /> Storage Used: 4.2 GB / 10 GB</div>
            <button onClick={handleSaveProject} className="px-4 py-2 bg-kala-secondary text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-kala-secondary/80 transition-colors"><Save size={16} /> Save</button>
          </div>
        </motion.div>

        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 flex items-start gap-3 mb-8">
          <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-200">{ADVANCED_MODERATION_WARNING_TEXT}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1 bg-kala-950/70 p-6 rounded-xl border border-kala-800 space-y-8">
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><Wand2 className="text-kala-accent" /> AI Toolbox</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-4">
                {tools.map((tool) => (
                  <Tool key={tool.name} {...tool} onClick={() => handleToolInteraction(tool.name)} />
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">My Resources</h2>
              <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`border-2 border-dashed rounded-2xl p-8 flex flex-col items-center justify-center transition-all relative ${isDragging ? 'border-kala-secondary bg-kala-secondary/10' : 'border-kala-700 bg-kala-800/30 hover:bg-kala-800/50'}`}>
                {isUploading ? (
                  <div className="text-center"><Loader2 className="w-10 h-10 text-kala-secondary animate-spin mx-auto mb-3" /><p className="text-white font-semibold">Scanning File...</p></div>
                ) : (
                  <><div className="w-12 h-12 bg-kala-800 rounded-full flex items-center justify-center mb-3 text-kala-400"><UploadCloud className="w-6 h-6" /></div><h3 className="text-base font-bold text-white mb-1">Drag & Drop or</h3><input type="file" id="file-upload" className="sr-only" onChange={handleFileSelect} /><label htmlFor="file-upload" className="text-sm text-kala-secondary font-semibold hover:underline cursor-pointer">Browse Files</label></>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {assets.map((asset) => (
                <div key={asset.id} className="p-2.5 bg-kala-900 rounded-lg flex items-center justify-between hover:bg-kala-800/80 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-md ${asset.type === 'Image' ? 'bg-purple-500/20 text-purple-400' : asset.type === 'Audio' ? 'bg-blue-500/20 text-blue-400' : asset.type === 'Document' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>
                      {asset.type === 'Image' ? <ImageIcon className="w-4 h-4" /> : asset.type === 'Audio' ? <Music className="w-4 h-4" /> : asset.type === 'Document' ? <FileText className="w-4 h-4" /> : <FileIcon className="w-4 h-4" />}
                    </div>
                    <div>
                      <h4 className="text-white font-medium text-sm leading-tight">{asset.name}</h4>
                      <p className="text-xs text-kala-500 mt-0.5">{asset.size}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                     <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${asset.status === 'Minted' ? 'bg-green-500/10 text-green-400 border-green-500/20' : asset.status === 'Fractionalized' ? 'bg-teal-500/10 text-teal-400 border-teal-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>{asset.status.toUpperCase()}</span>
                    {asset.status === 'Pinned' && (<button onClick={() => handleMintRequest(asset.id)} className={`text-xs text-white font-bold px-2 py-1 rounded transition-colors flex items-center gap-1 ${isConnected ? 'bg-kala-secondary hover:bg-cyan-400' : 'bg-kala-500 hover:bg-kala-400'}`}>{!isConnected && <Wallet className="w-3 h-3" />} Mint</button>)}
                    {asset.status === 'Minted' && (<button onClick={() => handleFractionalizeRequest(asset)} disabled={isProcessing} className="text-xs text-white font-bold px-2 py-1 rounded transition-colors flex items-center gap-1 bg-kala-accent hover:bg-orange-400 disabled:bg-kala-700"><GitCommit className="w-3 h-3" />Fractionalize</button>)}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div ref={drop} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="lg:col-span-2 bg-kala-950/70 p-6 rounded-xl border border-kala-800 min-h-[500px] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <input type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} className="bg-transparent text-2xl font-bold text-white border-none focus:ring-0 p-0" />
              <button onClick={handleClearCanvas} className="text-slate-400 hover:text-red-500 transition-colors flex items-center gap-2 text-sm font-medium"><Trash2 size={14} /> Clear Canvas</button>
            </div>
            <div className="flex-grow p-4 bg-kala-900 rounded-lg border-2 border-dashed border-kala-800/80 relative">
              {canvasTools.length === 0 ? (
                <div className="absolute inset-0 flex flex-col justify-center items-center text-center pointer-events-none">
                  <div className="w-16 h-16 rounded-full bg-kala-800 flex items-center justify-center mb-4"><PlusCircle className="w-8 h-8 text-kala-700" /></div>
                  <p className="text-slate-400 font-semibold">Drag or click AI tools to start</p>
                  <p className="text-sm text-slate-500">Combine AI modules to build your creative workflow</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{canvasTools.map((tool) => (<CanvasTool key={tool.id} {...tool} />))}</div>
              )}
            </div>
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Sparkles size={16} className="text-kala-accent" /> AI Output
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={handleCopyOutput} disabled={isGenerating || canvasTools.length === 0} className="text-kala-300 hover:text-white disabled:text-kala-600 disabled:cursor-not-allowed transition-colors p-1" title="Copy Output">
                    <Copy size={16} />
                  </button>
                  <button onClick={handleDownloadOutput} disabled={isGenerating || canvasTools.length === 0} className="text-kala-300 hover:text-white disabled:text-kala-600 disabled:cursor-not-allowed transition-colors p-1" title="Download Output">
                    <Download size={16} />
                  </button>
                </div>
              </div>
              <div className="p-4 h-48 bg-kala-900 rounded-lg text-slate-400 text-sm overflow-y-auto font-mono border border-kala-800">
                 {isGenerating ? (
                  <div className="flex items-center gap-2 text-kala-300">
                    <Loader2 className="w-4 h-4 animate-spin" /> 
                    <p className='whitespace-pre-wrap'>{aiOutput}</p>
                  </div>
                ) : (
                  <div className='whitespace-pre-wrap'>{aiOutput}</div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CreativeStudio;
